import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { PlusIcon, FilterIcon, EyeIcon, EditIcon, TrashIcon, CheckIcon } from 'lucide-react';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { Select } from '../../components/ui/Select';
import { DatePicker } from '../../components/ui/DatePicker';
import { Badge } from '../../components/ui/Badge';
interface BillPayment {
  id: string;
  voucherNo: string;
  billType: 'Internal' | 'External';
  expenseVendorLedger: string;
  paymentMethod: 'Cash' | 'Bank';
  amount: number;
  voucherDate: string;
  status: 'Pending' | 'Approved' | 'Rejected';
  createdBy: string;
  createdAt: string;
}
export function BillPaymentList() {
  const navigate = useNavigate();
  const [payments, setPayments] = useState<BillPayment[]>([]);
  const [loading, setLoading] = useState(true);
  const [showFilters, setShowFilters] = useState(true);
  const [filters, setFilters] = useState({
    dateFrom: '',
    dateTo: '',
    billType: '',
    paymentMethod: '',
    status: '',
    search: ''
  });
  // Mock user role
  const userRole = 'Finance Manager';
  useEffect(() => {
    loadPayments();
  }, [filters]);
  const loadPayments = async () => {
    setLoading(true);
    try {
      // Mock data
      const mockData: BillPayment[] = [{
        id: '1',
        voucherNo: 'BP-2024-001',
        billType: 'Internal',
        expenseVendorLedger: 'Office Supplies Expense',
        paymentMethod: 'Cash',
        amount: 15000,
        voucherDate: '2024-01-15',
        status: 'Pending',
        createdBy: 'John Doe',
        createdAt: '2024-01-15 10:30 AM'
      }, {
        id: '2',
        voucherNo: 'BP-2024-002',
        billType: 'External',
        expenseVendorLedger: 'ABC Suppliers (Vendor)',
        paymentMethod: 'Bank',
        amount: 85000,
        voucherDate: '2024-01-16',
        status: 'Approved',
        createdBy: 'Jane Smith',
        createdAt: '2024-01-16 02:15 PM'
      }];
      setPayments(mockData);
    } catch (error) {
      console.error('Failed to load bill payments:', error);
    } finally {
      setLoading(false);
    }
  };
  const handleDelete = async (id: string) => {
    if (confirm('Are you sure you want to delete this bill payment?')) {
      try {
        console.log('Deleting payment:', id);
        loadPayments();
      } catch (error: any) {
        alert(error.message);
      }
    }
  };
  const handleApprove = async (id: string) => {
    try {
      console.log('Approving payment:', id);
      loadPayments();
    } catch (error: any) {
      alert(error.message);
    }
  };
  const canEdit = (status: string) => {
    return status === 'Pending' && (userRole === 'Accountant' || userRole === 'Finance Manager');
  };
  const canDelete = (status: string) => {
    return status === 'Pending' && (userRole === 'Accountant' || userRole === 'Finance Manager');
  };
  const canApprove = () => {
    return userRole === 'Finance Manager';
  };
  const billTypeOptions = [{
    value: 'Internal',
    label: 'Internal'
  }, {
    value: 'External',
    label: 'External'
  }];
  const paymentMethodOptions = [{
    value: 'Cash',
    label: 'Cash'
  }, {
    value: 'Bank',
    label: 'Bank'
  }];
  const statusOptions = [{
    value: 'Pending',
    label: 'Pending'
  }, {
    value: 'Approved',
    label: 'Approved'
  }, {
    value: 'Rejected',
    label: 'Rejected'
  }];
  const getStatusVariant = (status: string): 'success' | 'warning' | 'danger' | 'default' => {
    const map: Record<string, 'success' | 'warning' | 'danger' | 'default'> = {
      Pending: 'warning',
      Approved: 'success',
      Rejected: 'danger'
    };
    return map[status] || 'default';
  };
  return <div className="min-h-screen bg-gray-50 p-6" data-id="element-749">
      <div className="max-w-7xl mx-auto space-y-6" data-id="element-750">
        {/* Header */}
        <div className="flex items-center justify-between" data-id="element-751">
          <div data-id="element-752">
            <h1 className="text-2xl font-bold text-gray-900" data-id="element-753">
              Bill Payment List
            </h1>
            <p className="text-sm text-gray-600 mt-1" data-id="element-754">
              Manage internal and external bill payments
            </p>
          </div>
          <div className="flex gap-2" data-id="element-755">
            <Button variant="secondary" onClick={() => setShowFilters(!showFilters)} data-id="element-756">
              <FilterIcon className="w-4 h-4 mr-2" data-id="element-757" />
              {showFilters ? 'Hide' : 'Show'} Filters
            </Button>
            {userRole !== 'Auditor' && <Button onClick={() => navigate('/bill-payment/create')} data-id="element-758">
                <PlusIcon className="w-4 h-4 mr-2" data-id="element-759" />
                Create Bill Payment
              </Button>}
          </div>
        </div>

        {/* Filters */}
        {showFilters && <div className="bg-white p-4 rounded-lg border space-y-4" data-id="element-760">
            <h3 className="text-sm font-semibold text-gray-900" data-id="element-761">
              Search Filters
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4" data-id="element-762">
              <Input label="Search" placeholder="Voucher No, Ledger, Amount..." value={filters.search} onChange={e => setFilters({
            ...filters,
            search: e.target.value
          })} data-id="element-763" />

              <Select label="Bill Type" options={billTypeOptions} value={filters.billType} onChange={e => setFilters({
            ...filters,
            billType: e.target.value
          })} data-id="element-764" />

              <Select label="Payment Method" options={paymentMethodOptions} value={filters.paymentMethod} onChange={e => setFilters({
            ...filters,
            paymentMethod: e.target.value
          })} data-id="element-765" />

              <Select label="Status" options={statusOptions} value={filters.status} onChange={e => setFilters({
            ...filters,
            status: e.target.value
          })} data-id="element-766" />

              <DatePicker label="Date From" value={filters.dateFrom} onChange={date => setFilters({
            ...filters,
            dateFrom: date
          })} data-id="element-767" />

              <DatePicker label="Date To" value={filters.dateTo} onChange={date => setFilters({
            ...filters,
            dateTo: date
          })} data-id="element-768" />
            </div>
            <div className="flex justify-end" data-id="element-769">
              <Button variant="secondary" size="sm" onClick={() => setFilters({
            dateFrom: '',
            dateTo: '',
            billType: '',
            paymentMethod: '',
            status: '',
            search: ''
          })} data-id="element-770">
                Clear Filters
              </Button>
            </div>
          </div>}

        {/* Table */}
        <div className="bg-white rounded-lg border overflow-hidden" data-id="element-771">
          {loading ? <div className="p-12 text-center text-gray-500" data-id="element-772">
              Loading bill payments...
            </div> : <div className="overflow-x-auto" data-id="element-773">
              <table className="min-w-full divide-y divide-gray-200" data-id="element-774">
                <thead className="bg-gray-50" data-id="element-775">
                  <tr data-id="element-776">
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase" data-id="element-777">
                      Voucher No
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase" data-id="element-778">
                      Bill Type
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase" data-id="element-779">
                      Expense/Vendor Ledger
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase" data-id="element-780">
                      Payment Method
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase" data-id="element-781">
                      Amount
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase" data-id="element-782">
                      Voucher Date
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase" data-id="element-783">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase" data-id="element-784">
                      Created By
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase" data-id="element-785">
                      Created At
                    </th>
                    <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase" data-id="element-786">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200" data-id="element-787">
                  {payments.length === 0 ? <tr data-id="element-788">
                      <td colSpan={10} className="px-6 py-12 text-center text-gray-500" data-id="element-789">
                        No bill payments found. Click "Create Bill Payment" to
                        add one.
                      </td>
                    </tr> : payments.map(payment => <tr key={payment.id} className="hover:bg-gray-50" data-id="element-790">
                        <td className="px-6 py-4" data-id="element-791">
                          <button onClick={() => navigate(`/bill-payment/${payment.id}`)} className="text-sm font-medium text-blue-600 hover:text-blue-800" data-id="element-792">
                            {payment.voucherNo}
                          </button>
                        </td>
                        <td className="px-6 py-4" data-id="element-793">
                          <span className={`inline-flex px-2 py-1 text-xs font-medium rounded ${payment.billType === 'Internal' ? 'bg-blue-100 text-blue-800' : 'bg-purple-100 text-purple-800'}`} data-id="element-794">
                            {payment.billType}
                          </span>
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-900" data-id="element-795">
                          {payment.expenseVendorLedger}
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-600" data-id="element-796">
                          {payment.paymentMethod}
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-900 text-right font-medium" data-id="element-797">
                          ${payment.amount.toLocaleString()}
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-900" data-id="element-798">
                          {new Date(payment.voucherDate).toLocaleDateString()}
                        </td>
                        <td className="px-6 py-4" data-id="element-799">
                          <Badge variant={getStatusVariant(payment.status)} data-id="element-800">
                            {payment.status}
                          </Badge>
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-600" data-id="element-801">
                          {payment.createdBy}
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-600" data-id="element-802">
                          {payment.createdAt}
                        </td>
                        <td className="px-6 py-4" data-id="element-803">
                          <div className="flex items-center justify-center gap-2" data-id="element-804">
                            <button onClick={() => navigate(`/bill-payment/${payment.id}`)} className="text-gray-600 hover:text-gray-900" title="View" data-id="element-805">
                              <EyeIcon className="w-4 h-4" data-id="element-806" />
                            </button>
                            {canEdit(payment.status) && <button onClick={() => navigate(`/bill-payment/${payment.id}/edit`)} className="text-gray-600 hover:text-gray-900" title="Edit" data-id="element-807">
                                <EditIcon className="w-4 h-4" data-id="element-808" />
                              </button>}
                            {canDelete(payment.status) && <button onClick={() => handleDelete(payment.id)} className="text-red-600 hover:text-red-800" title="Delete" data-id="element-809">
                                <TrashIcon className="w-4 h-4" data-id="element-810" />
                              </button>}
                            {canApprove() && payment.status === 'Pending' && <button onClick={() => handleApprove(payment.id)} className="text-green-600 hover:text-green-800" title="Approve" data-id="element-811">
                                <CheckIcon className="w-4 h-4" data-id="element-812" />
                              </button>}
                          </div>
                        </td>
                      </tr>)}
                </tbody>
              </table>
            </div>}
        </div>
      </div>
    </div>;
}